import { useStyles } from "./styles";
import { Box, Button, Grid2, Stack, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import constants from "../../../constants/constants.json";
import PropsTypes from "prop-types";
import BasicTable from "components/table";
import { useCallback, useContext, useEffect, useState } from "react";
import { getAllUsers } from "./users.services";
import { getToken } from "utils/loadAuth";
import { useAuth0 } from "@auth0/auth0-react";
import UserPermissionSettings from "components/userPermissionSettings";
import { useSearchParams } from "react-router-dom";
import { UserContext } from "context/userContext";
import SearchField from "components/simpleSearch";

const Users = ({ isNavbarOpen }) => {
  const theme = useTheme();
  const classes = useStyles({ theme, isNavbarOpen });
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [search, setSearch] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState([]);
  const [copiedUsers, setCopiedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState([]);

  const userContextValue = useContext(UserContext) || {};
  const loggedInUser = userContextValue.loggedInUser;
  const allowedUserTypes = [1, 2, 3, 4];

  useEffect(() => {
    const userDetailsParam = searchParams.get("userdetails");
    if (userDetailsParam) {
      setShowDetails(true);
      if (users.length > 0) {
        const user = users.find((u) => u.user_id === userDetailsParam);
        if (user) {
          setSelectedUser(user);
        }
      }
    } else {
      setShowDetails(false);
      setSelectedUser([]);
    }
  }, [searchParams, users]);

  const editHandler = (user) => {
    setSelectedUser(user);
    setSearchParams({ userdetails: user.user_id });
    setShowDetails(true);
  };

  const columns = [
    {
      field: "fullName",
      headerName: "Name",
      flex: 0.07,
      valueGetter: (_, row) => `${row?.firstname || ""} ${row?.lastname || ""}`,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 0.1,
    },
    {
      field: "products",
      headerName: "Products",
      flex: 0.06,
    },
    {
      field: "user_type",
      headerName: "User Type",
      flex: 0.05,
      valueGetter: (_, row) =>
        constants.access[row.user_type] || constants.access[4],
    },
  ];

  if (loggedInUser && loggedInUser.user_type !== 4) {
    columns.push({
      field: "Actions",
      headerName: "Actions",
      flex: 0.04,
      sortable: false,
      renderCell: (value) => {
        const canEdit =
          allowedUserTypes.includes(loggedInUser.user_type) &&
          value.row.user_type >= loggedInUser.user_type;
        return (
          <Box
            data-testid={"action-container"}
            className={classes.actionsContainer}
          >
            <Button
              data-testid={"edit-btn"}
              className={classes.actionBtn}
              onClick={() => editHandler(value.row)}
              disabled={!canEdit}
            >
              {constants.edit}
            </Button>
          </Box>
        );
      },
    });
  }

  const getUsersData = useCallback(async () => {
    setIsLoading(true);
    const token = await getToken({
      getAccessTokenSilently,
      loginWithRedirect,
    });
    const result = await getAllUsers({ token });
    if (result?.data) {
      setIsLoading(false);
      setUsers(result.data);
      setCopiedUsers(result.data);
    } else {
      setIsLoading(false);
      setUsers([]);
      setCopiedUsers([]);
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    getUsersData();
  }, [getUsersData]);

  const onSearchChange = (value) => {
    setSearch(value);
    if (!value) {
      setUsers(copiedUsers);
      return;
    }
    const filteredUsers = copiedUsers.filter(
      (user) =>
        user.firstname?.toLowerCase().includes(value.toLowerCase()) ||
        user.lastname?.toLowerCase().includes(value.toLowerCase())
    );
    setUsers(filteredUsers);
  };

  return (
    <Grid2 container sx={{ width: "100%" }} data-testid="users-container">
      <Grid2 size={2}></Grid2>
      {!showDetails ? (
        <Grid2 size={10} className={classes.wrapper}>
          <Box className={classes.tableWrapper}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography className={classes.title}>
                {constants.users}
              </Typography>
            </Box>
            <Stack className={classes.sectionsWrapper} spacing={1}>
              <Box sx={{ width: "100%" }}>
                <SearchField
                  searchValue={search}
                  setSearchValue={(e) => onSearchChange(e)}
                  placeholderText={"Search Users By Name"}
                />
                <BasicTable
                  disableColumnMenu
                  showBorder={false}
                  isLoading={isLoading}
                  columns={columns}
                  rows={users}
                />
              </Box>
            </Stack>
          </Box>
        </Grid2>
      ) : (
        <Grid2 size={10} className={classes.wrapper}>
          <UserPermissionSettings
            isNavbarOpen={isNavbarOpen}
            handleClose={() => {
              setShowDetails(false);
              setSearchParams({});
              setSearch("");
              getUsersData();
            }}
            user={selectedUser}
          />
        </Grid2>
      )}
    </Grid2>
  );
};

Users.propTypes = {
  isNavbarOpen: PropsTypes.bool,
};

export default Users;
